'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const API_URL = process.env.NEXT_PUBLIC_CYCLONE_API_URL || 'https://cyclone.agora.red/v1';
const BATCH = 24;

// Próximo turno de los negocios en pantalla, en lotes de hasta 24 (antes era un pedido por tarjeta).
// Cada tarjeta pide el suyo y los pedidos de un mismo instante viajan juntos.
// `dates[username]`: 'YYYY-MM-DD', null (sin turnos) o undefined (todavía no llegó).
const NextAvailableContext = createContext(null);

export function NextAvailableProvider({ children }) {
  const [dates, setDates] = useState({});
  const [failed, setFailed] = useState(false);
  const requested = useRef(new Set());
  const queue = useRef([]);
  const timer = useRef(null);

  const flush = useCallback(() => {
    timer.current = null;
    const pending = queue.current.splice(0);
    for (let i = 0; i < pending.length; i += BATCH) {
      const chunk = pending.slice(i, i + BATCH);
      fetch(`${API_URL}/vendors/marketplace/next-available?usernames=${encodeURIComponent(chunk.join(','))}`)
        .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
        .then((data) => setDates((prev) => ({ ...prev, ...(data?.dates || {}) })))
        // API sin el lote: cada tarjeta vuelve a pedir lo suyo.
        .catch(() => setFailed(true));
    }
  }, []);

  const request = useCallback(
    (username) => {
      if (!username || requested.current.has(username)) return;
      requested.current.add(username);
      queue.current.push(username);
      if (!timer.current) timer.current = setTimeout(flush, 40);
    },
    [flush]
  );

  useEffect(() => () => clearTimeout(timer.current), []);

  const value = useMemo(() => ({ dates, failed, request }), [dates, failed, request]);
  return <NextAvailableContext.Provider value={value}>{children}</NextAvailableContext.Provider>;
}

/** Hoy en Argentina (no en UTC: a las 21 h ya sería mañana). */
export const todayInArgentina = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });

/**
 * Próximo turno de varios negocios a la vez (el filtro «Turnos hoy» de /negocios). Pide los que
 * falten y devuelve el mapa `username → fecha`. Sin proveedor, un mapa vacío.
 */
export function useNextAvailableDates(usernames) {
  const batch = useContext(NextAvailableContext);
  const key = usernames.join(',');
  useEffect(() => {
    if (!batch || !key) return;
    key.split(',').forEach((u) => batch.request(u));
  }, [batch, key]);
  return batch?.dates || EMPTY_DATES;
}

const EMPTY_DATES = {};

/** Próximo turno de un negocio: del lote si hay proveedor; si no, un pedido propio. */
export function useNextAvailable(username) {
  const batch = useContext(NextAvailableContext);
  const [own, setOwn] = useState(undefined);
  const needsOwn = !batch || batch.failed;

  useEffect(() => {
    if (!needsOwn && username) batch.request(username);
  }, [needsOwn, username, batch]);

  useEffect(() => {
    if (!needsOwn || !username) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/vendors/${username}/available_dates/next`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => !cancelled && setOwn(data?.date || null))
      .catch(() => !cancelled && setOwn(null));
    return () => {
      cancelled = true;
    };
  }, [needsOwn, username]);

  return needsOwn ? own : batch.dates[username];
}
